import { useState } from 'react';

import {
  useUsers,
  useAdjustBalance,
  useUpdateRole,
  useSetDisabled,
  useLogoutAll,
  useUpdatePermissions,
  useDeleteUser,
  useCreateUser,
  useResetPassword,
} from '@/hooks/useUsers';
import { useOrgs } from '@/hooks/useOrgs';
import { useAuth } from '@/auth/AuthContext';
import { ApiError } from '@/api/client';
import { Loading, ErrorBanner, EmptyState } from '@/components/common/States';
import { fmtDuration, shortId } from '@/lib/format';
import { isAdmin, isSystemAdmin, type Role, type User } from '@/types';

const msgOf = (e: unknown) => (e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Action failed');

const ROLE_LABEL: Record<Role, string> = {
  system_admin: 'System admin',
  org_admin: 'Company admin',
  member: 'Member',
};

// Roles the actor is allowed to assign.
function assignableRoles(me: User | null): Role[] {
  if (isSystemAdmin(me)) return ['system_admin', 'org_admin', 'member'];
  if (me?.role === 'org_admin') return ['org_admin', 'member'];
  return [];
}

function BalanceControl({ user, sys, onErr }: { user: User; sys: boolean; onErr: (m: string) => void }) {
  const adjust = useAdjustBalance();
  const [secs, setSecs] = useState(3600);
  const busy = adjust.isPending && adjust.variables?.id === user.id;
  const apply = (sign: 1 | -1) =>
    adjust.mutate({ id: user.id, delta_seconds: sign * Math.abs(secs || 0) }, { onError: (e) => onErr(msgOf(e)) });
  return (
    <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
      <button className="btn btn-ghost" disabled={busy} onClick={() => apply(-1)} title={sys ? 'subtract' : 'take back to your balance'}>−</button>
      <input className="input mono" type="number" min={0} value={secs} onChange={(e) => setSecs(Number(e.target.value))} style={{ width: 82, padding: '6px 8px', textAlign: 'right' }} />
      <span className="muted mono" style={{ fontSize: 11 }}>s</span>
      <button className="btn btn-ghost" style={{ color: 'var(--signal)' }} disabled={busy} onClick={() => apply(1)} title={sys ? 'add' : 'give from your balance'}>+</button>
      {busy && <span className="spinner" />}
    </div>
  );
}

function GrantBox({ user, field, label, onErr }: { user: User; field: 'can_delete_sessions' | 'can_delete_users'; label: string; onErr: (m: string) => void }) {
  const permM = useUpdatePermissions();
  const admin = isAdmin(user);
  const busy = permM.isPending && permM.variables?.id === user.id;
  const checked = admin || user[field];
  return (
    <label className="row" style={{ gap: 6, alignItems: 'center', opacity: busy ? 0.5 : 1, cursor: admin ? 'not-allowed' : 'pointer' }} title={admin ? 'admins can always delete' : `allow this user to remove ${label}`}>
      <input type="checkbox" checked={checked} disabled={admin || busy} onChange={(e) => permM.mutate({ id: user.id, perms: { [field]: e.target.checked } }, { onError: (er) => onErr(msgOf(er)) })} />
      <span className="mono" style={{ fontSize: 11 }}>{label}</span>
    </label>
  );
}

function CreateUser({ me, onErr, onDone }: { me: User | null; onErr: (m: string) => void; onDone: (m: string) => void }) {
  const createUser = useCreateUser();
  const orgs = useOrgs();
  const sys = isSystemAdmin(me);
  const roles = assignableRoles(me);
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('member');
  const [org, setOrg] = useState('');

  async function submit() {
    if (!email || pw.length < 8) return onErr('Email and an 8+ char password are required.');
    if (sys && role !== 'system_admin' && !org) return onErr('Pick an organization.');
    try {
      await createUser.mutateAsync({
        email, password: pw, display_name: name || null, role,
        org_id: sys ? (role === 'system_admin' ? null : org) : (me?.org_id ?? null),
      });
      setEmail(''); setPw(''); setName('');
      onDone(`Created ${ROLE_LABEL[role].toLowerCase()} ${email}.`);
    } catch (e) { onErr(msgOf(e)); }
  }

  return (
    <div className="card" style={{ marginBottom: 18, display: 'grid', gap: 12 }}>
      <div className="eyebrow">new user</div>
      <div className="row wrap" style={{ gap: 12, alignItems: 'flex-end' }}>
        <div className="field grow" style={{ minWidth: 200 }}><label className="label">Email</label><input className="input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@company.com" /></div>
        <div className="field" style={{ minWidth: 150 }}><label className="label">Name (optional)</label><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div className="field" style={{ minWidth: 130 }}><label className="label">Role</label>
          <select className="select" value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {roles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
          </select>
        </div>
        {sys && role !== 'system_admin' && (
          <div className="field" style={{ minWidth: 160 }}><label className="label">Organization</label>
            <select className="select" value={org} onChange={(e) => setOrg(e.target.value)}>
              <option value="">— select org —</option>
              {(orgs.data ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
        )}
        <div className="field" style={{ minWidth: 160 }}><label className="label">Temp password (8+)</label><input className="input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} /></div>
        <button className="btn btn-primary" onClick={submit} disabled={createUser.isPending}>{createUser.isPending ? <span className="spinner" /> : 'Create user'}</button>
      </div>
    </div>
  );
}

export function Users() {
  const { user: me } = useAuth();
  const { data, isLoading, error } = useUsers();
  const orgs = useOrgs();
  const roleM = useUpdateRole();
  const disM = useSetDisabled();
  const logoutM = useLogoutAll();
  const delM = useDeleteUser();
  const resetM = useResetPassword();
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  if (!isAdmin(me)) {
    return <EmptyState title="Admins only" hint="You don't have access to user administration." />;
  }

  const onErr = (m: string) => { setActionErr(m); setNote(null); };
  const sys = isSystemAdmin(me);
  const roleOpts = assignableRoles(me);
  const orgName = (id: string | null) => (orgs.data ?? []).find((o) => o.id === id)?.name ?? (id ? shortId(id) : '—');

  return (
    <div className="fade-up">
      <div className="page-head spread">
        <div>
          <div className="eyebrow">administration</div>
          <div className="title">Users</div>
          <div className="sub">{sys ? 'All accounts across every organization.' : 'Your organization’s accounts.'} Balance ≤ 0 blocks test starts.</div>
        </div>
        {data && <div className="card kpi" style={{ minWidth: 120 }}><div className="k-label">accounts</div><div className="k-value" style={{ fontSize: 24 }}>{data.length}</div></div>}
      </div>

      {actionErr && <div style={{ marginBottom: 16 }} onClick={() => setActionErr(null)}><ErrorBanner error={actionErr} /></div>}
      {note && <div className="card" style={{ marginBottom: 16, borderLeft: '3px solid var(--signal)' }}><span className="mono" style={{ fontSize: 13 }}>{note}</span></div>}

      <CreateUser me={me} onErr={onErr} onDone={(m) => { setNote(m); setActionErr(null); }} />

      {isLoading ? (
        <Loading label="Loading users" />
      ) : error ? (
        <ErrorBanner error={error} />
      ) : !data || data.length === 0 ? (
        <EmptyState title="No users" />
      ) : (
        <div className="panel">
          <table className="table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                {sys && <th>Org</th>}
                <th>Delete grants</th>
                <th>Balance</th>
                <th>Active</th>
                <th>{sys ? 'Adjust balance' : 'Give / take'}</th>
                <th className="right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map((u) => {
                const self = u.id === me?.id;
                const roleBusy = roleM.isPending && roleM.variables?.id === u.id;
                const disBusy = disM.isPending && disM.variables?.id === u.id;
                return (
                  <tr key={u.id} style={{ opacity: u.disabled ? 0.6 : 1 }}>
                    <td className="mono">
                      {u.email}
                      {self && <span className="muted mono" style={{ fontSize: 11, marginLeft: 8 }}>you</span>}
                    </td>
                    <td>
                      <select
                        className="pill-select"
                        value={u.role}
                        disabled={self || roleBusy || (!sys && u.role === 'system_admin')}
                        title={self ? "can't change your own role" : 'change role'}
                        onChange={(e) => roleM.mutate({ id: u.id, role: e.target.value as Role }, { onError: (er) => onErr(msgOf(er)) })}
                      >
                        {/* keep the current value selectable even if not in the actor's assignable set */}
                        {(roleOpts.includes(u.role) ? roleOpts : [u.role, ...roleOpts]).map((r) => (
                          <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                        ))}
                      </select>
                    </td>
                    {sys && <td className="mono dim" style={{ fontSize: 12 }}>{u.role === 'system_admin' ? '— system —' : orgName(u.org_id)}</td>}
                    <td>
                      <div className="stack" style={{ gap: 4 }}>
                        <GrantBox user={u} field="can_delete_sessions" label="sessions" onErr={onErr} />
                        <GrantBox user={u} field="can_delete_users" label="users" onErr={onErr} />
                      </div>
                    </td>
                    <td className="mono" style={{ color: u.traffic_seconds_balance > 0 ? 'var(--signal)' : 'var(--loss)' }}>{fmtDuration(u.traffic_seconds_balance)}</td>
                    <td>
                      <button
                        className={`switch${u.disabled ? '' : ' on'}`}
                        disabled={self || disBusy}
                        title={self ? "can't disable yourself" : u.disabled ? 'enable' : 'disable'}
                        onClick={() => disM.mutate({ id: u.id, disabled: !u.disabled }, { onError: (er) => onErr(msgOf(er)) })}
                      />
                    </td>
                    <td><BalanceControl user={u} sys={sys} onErr={onErr} /></td>
                    <td className="right">
                      <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                        <button
                          className="btn btn-ghost"
                          title="set a new password for this user"
                          onClick={() => {
                            const np = window.prompt(`New password for ${u.email} (min 8 chars):`);
                            if (!np) return;
                            if (np.length < 8) return onErr('Password must be at least 8 characters.');
                            resetM.mutate({ id: u.id, new_password: np }, { onError: (er) => onErr(msgOf(er)), onSuccess: () => { setActionErr(null); setNote(`Password reset for ${u.email}.`); } });
                          }}
                        >
                          Reset pw
                        </button>
                        <button className="btn btn-ghost" title="revoke all sessions (force re-login)" onClick={() => logoutM.mutate(u.id, { onError: (er) => onErr(msgOf(er)) })}>Force logout</button>
                        <button
                          className="btn btn-ghost"
                          style={{ color: 'var(--loss)' }}
                          disabled={self || (delM.isPending && delM.variables === u.id)}
                          title={self ? "can't delete yourself" : 'delete user'}
                          onClick={() => {
                            if (self) return;
                            if (!window.confirm(`Delete ${u.email}? Their sessions are kept (ownership cleared); agent tokens are removed. This cannot be undone.`)) return;
                            delM.mutate(u.id, { onError: (er) => onErr(msgOf(er)) });
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
